import React, { FC } from 'react'

import { useTranslation } from '@/hooks/useDebugTranslations'

import FilterChip from './FilterChip'

const TIME_INTERVALS = {
  AFTERNOON: '12-18',
  EVENING: '18-24',
  MORNING: '06-12',
  NIGHT: '00-06',
}

const TIME_INTERVALS_LONG = {
  AFTERNOON: '12:00 - 18:00',
  EVENING: '18:00 - 24:00',
  MORNING: '06:00 - 12:00',
  NIGHT: '00:00 - 06:00',
}

interface Props {
  onTimeFilterChange: (filters: TimeFilter) => void
  returnLeg?: boolean
  selectedTimeFilters: TimeFilter
}

function getSelectedIntervals(filters: TimeFilter, intervals = TIME_INTERVALS_LONG): string {
  const mapping: Record<keyof TimeFilter, string> = {
    afternoon: intervals.AFTERNOON,
    all: '', // not displayed individually
    evening: intervals.EVENING,
    morning: intervals.MORNING,
    night: intervals.NIGHT,
  }
  if (filters.all) return ''

  return (['night', 'morning', 'afternoon', 'evening'] as const)
    .filter((key) => filters[key])
    .map((key) => mapping[key])
    .join(', ')
}

const TimeFilters: FC<Props> = ({ onTimeFilterChange, returnLeg, selectedTimeFilters }): React.ReactElement => {
  const { t } = useTranslation()

  const handleToggle = (timePeriod: keyof TimeFilter) => {
    if (timePeriod === 'all') {
      onTimeFilterChange({ afternoon: false, all: true, evening: false, morning: false, night: false })
    } else {
      const newFilters = { ...selectedTimeFilters, all: false, [timePeriod]: !selectedTimeFilters[timePeriod] }
      const noneSelected = !newFilters.night && !newFilters.morning && !newFilters.afternoon && !newFilters.evening
      if (noneSelected) {
        onTimeFilterChange({ afternoon: false, all: true, evening: false, morning: false, night: false })
      } else {
        onTimeFilterChange(newFilters)
      }
    }
  }

  const selectedIntervals = getSelectedIntervals(selectedTimeFilters)

  return (
    <div className="flex flex-col">
      <div className="gap-semantic-global-sm flex max-w-full flex-row flex-nowrap items-stretch">
        <FilterChip
          onToggle={() => handleToggle('night')}
          resourceKey="TIME-FILTER-SHOW-NIGHT"
          returnLeg={returnLeg}
          selected={selectedTimeFilters.night}
          timeInterval={TIME_INTERVALS.NIGHT}
        />
        <FilterChip
          onToggle={() => handleToggle('morning')}
          resourceKey="TIME-FILTER-SHOW-MORNING"
          returnLeg={returnLeg}
          selected={selectedTimeFilters.morning}
          timeInterval={TIME_INTERVALS.MORNING}
        />
        <FilterChip
          onToggle={() => handleToggle('afternoon')}
          resourceKey="TIME-FILTER-SHOW-AFTERNOON"
          returnLeg={returnLeg}
          selected={selectedTimeFilters.afternoon}
          timeInterval={TIME_INTERVALS.AFTERNOON}
        />
        <FilterChip
          onToggle={() => handleToggle('evening')}
          resourceKey="TIME-FILTER-SHOW-EVENING"
          returnLeg={returnLeg}
          selected={selectedTimeFilters.evening}
          timeInterval={TIME_INTERVALS.EVENING}
        />
      </div>
      {selectedIntervals ? (
        <div className="typography-body-xs-regular text-semantic-global-text-light mt-semantic-global-2xs">
          {`${t('SHOWING-DEPARTURES-FROM')}: ${selectedIntervals}`}
        </div>
      ) : null}
    </div>
  )
}

export default TimeFilters

export interface TimeFilter {
  afternoon: boolean
  all: boolean
  evening: boolean
  morning: boolean
  night: boolean
}
